import { z } from 'zod';

import { zMessageUnionTypeSchema } from '@/types/schema';

// S3 data schema
export const S3DataSchema = z.object({
  presigned_url: z.string(),
  storage_url: z.string().optional(),
  s3_url: z.string().optional(), // Alternative naming
});

// LangChain serialized constructor schema
export const SerializedConstructorSchema = z.object({
  lc: z.number(),
  type: z.literal('constructor'),
  id: z.array(z.string()),
  kwargs: z.record(z.unknown()).optional(),
  name: z.string().optional(),
});

// Invocation params schema
const InvocationParamsSchema = z
  .object({
    model: z.string().optional(),
    model_name: z.string().optional(),
    temperature: z.union([z.number(), z.null()]).optional(),
    top_p: z.union([z.number(), z.null()]).optional(),
    frequency_penalty: z.union([z.number(), z.null()]).optional(),
    presence_penalty: z.union([z.number(), z.null()]).optional(),
    max_tokens: z.union([z.number(), z.null()]).optional(),
    n: z.union([z.number(), z.null()]).optional(),
    stream: z.union([z.boolean(), z.null()]).optional(),
    stop: z.union([z.string(), z.array(z.string()), z.null()]).optional(),
    logit_bias: z.record(z.number()).optional(),
    _type: z.string().optional(),
  })
  .passthrough();

// Extra schema for runs
const ExtraSchema = z
  .object({
    invocation_params: InvocationParamsSchema.optional(),
    options: z.record(z.unknown()).optional(),
    metadata: z.record(z.unknown()).optional(),
    batch_size: z.union([z.number(), z.null()]).optional(),
  })
  .passthrough();

// Messages schema
const MessagesSchema = z.array(zMessageUnionTypeSchema);

export const PlaygroundCompatibleRunSchema = z
  .object({
    // Required fields
    id: z.string(),
    run_type: z.enum(['llm', 'prompt']),

    // Optional basic fields
    name: z.string().optional(),
    status: z
      .enum(['pending', 'error', 'success', 'interrupted'])
      .nullable()
      .optional(),
    start_time: z.string().optional(),
    end_time: z.string().optional(),
    trace_id: z.string().optional(),
    dotted_order: z.string().optional(),
    session_id: z.string().optional(),
    app_path: z.string().optional(),
    error: z.string().nullable().optional(),
    tags: z.array(z.string()).optional(),

    // Input data
    inputs: z
      .union([
        // LangChain message format (most common)
        z.object({
          messages: z.union([
            z.array(MessagesSchema), // Array of message arrays (nested format)
            MessagesSchema, // Direct array of messages
          ]),
        }),
        // Prompt template inputs - simple key-value pairs, no messages
        z
          .record(z.union([z.string(), z.number(), z.boolean()]))
          .refine(
            (obj) => !('messages' in obj) || Array.isArray(obj.messages),
            {
              message: "If 'messages' key exists, it must be an array",
            }
          ),
        // Null when inputs are stored in S3
        z.null(),
      ])
      .optional(),

    // S3 URLs for inputs when they're too large for direct storage
    inputs_s3_urls: z
      .union([S3DataSchema, z.record(S3DataSchema), z.null()])
      .optional(),

    // Serialized manifest validation
    serialized: z.union([SerializedConstructorSchema, z.null()]).optional(),

    // S3 URLs for large serialized data
    s3_urls: z
      .union([S3DataSchema, z.record(S3DataSchema), z.null()])
      .optional(),

    // Additional fields that may affect playground compatibility
    extra: z.union([ExtraSchema, z.null()]).optional(),
  })
  .passthrough();

// Validation function for run compatibility
export const validateRunForPlayground = (
  run: unknown
): {
  isValid: boolean;
  errors?: string[];
  data?: z.infer<typeof PlaygroundCompatibleRunSchema>;
} => {
  try {
    const result = PlaygroundCompatibleRunSchema.safeParse(run);
    if (result.success) {
      return { isValid: true, data: result.data };
    }
    return {
      isValid: false,
      errors: result.error.errors.map((err) => {
        const path = err.path.join('.');
        let detailedMessage = `${path}: ${err.message}`;

        if (path.startsWith('inputs')) {
          const inputsValue = (run as Record<string, unknown>)?.inputs;
          if (inputsValue !== undefined) {
            const inputType = Array.isArray(inputsValue)
              ? 'array'
              : typeof inputsValue;

            // For top-level inputs errors, show the problematic field and its type
            if (
              path === 'inputs' &&
              inputsValue &&
              typeof inputsValue === 'object'
            ) {
              const keys = Object.keys(inputsValue);
              const problemField = keys[0]; // Just take the first field

              if (problemField) {
                const fieldValue = (inputsValue as Record<string, unknown>)[
                  problemField
                ];
                const fieldType = Array.isArray(fieldValue)
                  ? 'array'
                  : typeof fieldValue;
                detailedMessage += ` at ${problemField} (received type: ${fieldType})`;
              } else {
                detailedMessage += ` (received type: ${inputType})`;
              }
            } else {
              detailedMessage += ` (received type: ${inputType})`;
            }
          }
        }

        return detailedMessage;
      }),
    };
  } catch (error) {
    return {
      isValid: false,
      errors: [
        `Validation error: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      ],
    };
  }
};

export type PlaygroundCompatibleRun = z.infer<
  typeof PlaygroundCompatibleRunSchema
>;
